import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { StyleSheet, TextInputProps } from 'react-native';

import Text from '../Text';
import TextInputAutocomplete, { PredictionType } from './TextIputAutocomplete';

export type InputAutocompleteFieldProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  onChangeText: (text: string) => void;
  loading?: boolean;
  predictions: PredictionType[];
  showPredictions: boolean;
  onPredictionTapped: ({ value, label }: PredictionType) => void;
  leadingIcon?: React.ReactNode;
  traillingIcon?: React.ReactNode;
  closeSuggestions?: boolean;
  onCloseSuggestions?: () => void;
  variant?: 'default' | 'rounded_with_shadow';
  showError?: boolean;
  label?: string;
  value: string;
  placeholder?: string;
  predictionsScrollEnabled?: boolean;
  showNoResult?: boolean;
  extraPredictionComponent?: React.ReactElement;
  extraPredictionItemHeight?: number;
  extraPredictionFullWidth?: boolean;
  autoFocus?: boolean;
  onKeyPress?: TextInputProps['onKeyPress'];
  onSubmitEditing?: TextInputProps['onSubmitEditing'];
  onFocus?: TextInputProps['onFocus'];
  onBlur?: TextInputProps['onBlur'];
  submitBehavior?: TextInputProps['submitBehavior'];
};

const InputAutocompleteField = <T extends FieldValues>({
  name,
  control,
  onChangeText,
  loading,
  predictions,
  showPredictions,
  onPredictionTapped,
  label,
  value,
  placeholder,
  leadingIcon,
  traillingIcon,
  closeSuggestions = false,
  onCloseSuggestions,
  variant = 'default',
  showError = true,
  predictionsScrollEnabled = true,
  showNoResult = true,
  extraPredictionComponent,
  extraPredictionItemHeight,
  extraPredictionFullWidth,
  onKeyPress,
  onSubmitEditing,
  onFocus,
  onBlur,
  submitBehavior
}: InputAutocompleteFieldProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          {label && (
            <Text fontWeight="700" style={styles.label}>
              {label}
            </Text>
          )}
          <TextInputAutocomplete
            value={value}
            placeholder={placeholder}
            onChangeText={(text) => {
              onChangeText(text);
            }}
            loading={loading}
            leadingIcon={leadingIcon}
            traillingIcon={traillingIcon}
            closeSuggestions={closeSuggestions}
            onCloseSuggestions={onCloseSuggestions}
            variant={variant}
            predictions={predictions}
            showPredictions={showPredictions}
            onPredictionTapped={({ value, label }) => {
              onPredictionTapped({ value, label });
              field.onChange(label);
            }}
            predictionsScrollEnabled={predictionsScrollEnabled}
            showNoResult={showNoResult}
            extraPredictionComponent={extraPredictionComponent}
            extraPredictionItemHeight={extraPredictionItemHeight}
            extraPredictionFullWidth={extraPredictionFullWidth}
            onKeyPress={onKeyPress}
            onSubmitEditing={onSubmitEditing}
            onFocus={onFocus}
            onBlur={onBlur}
            submitBehavior={submitBehavior}
          />
          {showError && !!error?.message && <Text style={styles.errorText}>{error.message}</Text>}
        </>
      )}
    />
  );
};

export default InputAutocompleteField;

const styles = StyleSheet.create({
  errorText: {
    marginTop: 10,
    fontSize: 16,
    color: 'red'
  },

  label: {
    color: 'black',
    marginBottom: 10
  }
});
